import React from 'react';

import { Stack, HStack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';

import { Input, Select, Divider } from 'components';
import { Datepicker } from 'components/Datepicker';
import { ShippingTypes } from 'enums/types';
import { debounceShipping } from 'utils/debounces';
import { maskKm, maskTime } from 'utils/masks';

import BatchesForm from './Batch';
import DriverForm from './Driver';
import Partner from './Partner';

function SenderForm({ register, control }) {
  return (
    <Stack spaciong={4}>
      <Input
        {...register('sourceName')}
        label="Remetente"
        placeholder="Ex: Hospital Vaz Med LTDA"
      />
      <Input
        {...register('destinationName')}
        label="Destinatário"
        placeholder="Ex: Hospital Vaz Med LTDA"
      />
      <HStack justify="space-between">
        <Input
          {...register('operator')}
          viewProps={{ w: '60%' }}
          label="Operadora"
          placeholder="Ex: Latam"
        />
        <Input
          {...register('code')}
          viewProps={{ w: '37%' }}
          label="Código do Vôo"
          placeholder="Ex: FF000"
        />
      </HStack>
      <HStack justify="space-between">
        <Controller
          defaultValue={{ label: null, id: null }}
          key="modality"
          control={control}
          name="modality"
          render={({ field }) => (
            <Select
              viewProps={{ w: '60%' }}
              loadOptions={debounceShipping}
              defaultOptions={Object.values(ShippingTypes).map(
                (type) => ({ label: type, value: type }))}
              label="Modalidade"
              {...field}
            />
          )}
        />
        <Input
          {...register('consignee')}
          viewProps={{ w: '37%' }}
          label="Consignatário"
          placeholder="Ex: Disgnósticos Livre LTDA"
        />
      </HStack>
      <Input
        viewProps={{ w: '60%' }}
        label="Volume previsto"
        {...register('estimatedVolume')}
        placeholder="Volume previsto de recebimento de material."
      />
    </Stack>
  );
}

function ShippingForm({
  register, getValues, control, setValue, watch,
}) {
  return (
    <Stack
      spacing={4}
      flexDir="column">
      <HStack justify="space-between">
        <Controller
          name="boardingAt"
          control={control}
          rules={{
            onChange(event) {
              if (event.target.value.length >= 10) {
                const format = event.target.value.length === 10 ? 'DD/MM/YYYY' : undefined;
                const date = dayjs(event.target.value, format);
                if (date.isValid()) {
                  setValue('boardingAt', date);
                }
              }
            },
          }}
          render={({ field }) => (
            <Datepicker
              label="Data do Despacho"
              placeholder="00/00/0000"
              {...field}
            />
          )}
        />
        <Input
          {...register('boardingTime')}
          label="Horário do Despacho"
          placeholder="00:00"
          onChange={({ target: { value } }) => {
            setValue('boardingTime',
              maskTime.resolve(value),
            );
          }}
        />
      </HStack>
      <DriverForm
        control={control}
        getValues={getValues}
        setValue={setValue}
        watch={watch}
        drivers={{
          name: 'drivers',
          label: 'Condutores',
        }}
        vehicle={{
          name: 'vehicle',
          label: 'Veículo',
        }}
      />
      <Input
        {...register('estimatedKm')}
        viewProps={{ w: '60%', mt: 5 }}
        label="KM Previsto"
        placeholder="000 Km"
        onChange={({ target: { value } }) => {
          setValue('estimatedKm',
            maskKm.resolve(value),
          );
        }}
      />
    </Stack>
  );
}

function RecipientForm({
  register, control, getValues, setValue, watch,
}) {
  return (
    <Stack spacing={4}>
      <DriverForm
        control={control}
        getValues={getValues}
        setValue={setValue}
        watch={watch}
        except={watch('drivers')}
        drivers={{
          // Gambiarra pra resolver um bug de render
          // que ocorre ESPECIFICAMENTE quando a label é destinationDrivers
          // followupDrivers === destinationDrivers
          // followupVehicle === destinationVehicle
          name: 'followupDrivers',
          label: 'Condutores',
        }}
        vehicle={{
          name: 'followupVehicle',
          label: 'Veículo',
        }}
      />
      <HStack justify="space-between">
        <Controller
          name="collectAt"
          control={control}
          rules={{
            onChange(event) {
              if (event.target.value.length >= 10) {
                const format = event.target.value.length === 10 ? 'DD/MM/YYYY' : undefined;
                const date = dayjs(event.target.value, format);
                if (date.isValid()) {
                  setValue('collectAt', date);
                }
              }
            },
          }}
          render={({ field }) => (
            <Datepicker
              label="Data da Coleta"
              placeholder="00/00/0000"
              {...field}
            />
          )}
        />
        <Input
          {...register('collectTime')}
          label="Horário de Coleta"
          placeholder="00:00"
          onChange={({ target: { value } }) => {
            setValue('collectTime',
              maskTime.resolve(value),
            );
          }}
        />
      </HStack>
      <Input
        {...register('collectEstimatedKm')}
        viewProps={{ w: '60%', mt: 5 }}
        label="KM Previsto"
        placeholder="000 Km"
        onChange={({ target: { value } }) => {
          setValue('collectEstimatedKm',
            maskKm.resolve(value),
          );
        }}
      />
    </Stack>
  );
}

export default function DispatchForm(form) {
  return (
    <Stack spacing={4}>
      <SenderForm {...form} />
      <Divider mb={8} />
      <Partner
        {...form}
        label="Local de Origem"
        name="dispatchSource"
      />
      <ShippingForm
        {...form}
      />
      <Divider mb={8} />
      <Partner
        {...form}
        label="Local de Destino"
        name="dispatchDestination"
      />
      <RecipientForm {...form} />
      <Divider mb={8} />
      <BatchesForm
        name="collectActivities"
        {...form}
      />
    </Stack>
  );
}
